/**
 * claraApi.js
 *
 * RTK Query API slice for Clara endpoints — live data from Databricks clara_silver.
 *
 * Provides:
 * useFetchClaraDomainsQuery()
 * – Calls GET /clara/domains
 * – Returns domain list for the megamenu
 *
 * useFetchClaraDomainUseCasesQuery(domainId)
 * – Calls GET /clara/solutions/:domainId
 * – Returns use-case cards only (no API details). Fast initial page load.
 *
 * useFetchUseCaseApisQuery(useCaseId)
 * – Calls GET /clara/usecases/:useCaseId/apis
 * – Returns flat api list (uri, method, openapiSummary, specStatusCode, canonicalTitle).
 * Fast — no SOR join. Loaded when user opens "Associated APIs".
 *
 * useFetchUseCaseApisSorQuery(useCaseId)
 * – Calls GET /clara/usecases/:useCaseId/sor
 * – Returns SOR enrichment rows (sorName, sorApiStatus, deliveryDate, relatedDocGuideUrl)
 * keyed by uri+method. Loaded in parallel; consumed by Responses/Guides tabs.
 */

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const OPERATION_DETAILS_ORIGINS = {
  IAT: "https://marketplace-apis-iat.adp.com",
  PROD: "https://marketplace-apis.adp.com",
};

function resolveOperationDetailsOrigin() {
  const environment = (import.meta.env.VITE_ENVIRONMENT ?? "").toUpperCase();

  if (OPERATION_DETAILS_ORIGINS[environment]) {
    return OPERATION_DETAILS_ORIGINS[environment];
  }

  if (typeof window !== "undefined") {
    if (window.location.hostname === "developers.adp.com") {
      return OPERATION_DETAILS_ORIGINS.PROD;
    }

    if (window.location.hostname.includes("iat-developers.adp.com")) {
      return OPERATION_DETAILS_ORIGINS.IAT;
    }
  }

  return OPERATION_DETAILS_ORIGINS.IAT;
}

const claraApi = createApi({
  reducerPath: "claraApi",
  baseQuery: fetchBaseQuery({
    prepareHeaders: (headers) => {
      headers.append("Content-Type", "application/json");
      headers.append("X-Requested-With", "XMLHttpRequest");
      headers.append("Accept", "application/json");
    },
    credentials: "include",
  }),
  endpoints(builder) {
    return {
      fetchClaraDomains: builder.query({
        query: () => ({
          url: `${import.meta.env.VITE_API_PATH}/clara/domains`,
          method: "GET",
        }),
        transformResponse: (response) =>
          response?.data?.length ? response.data : null,
      }),

      fetchClaraDomainUseCases: builder.query({
        query: (domainId) => ({
          url: `${import.meta.env.VITE_API_PATH}/clara/solutions/${encodeURIComponent(domainId)}`,
          method: "GET",
        }),
        // getDatabricksDomainUseCases returns { source: { title, region, content: { section1, section2 } } }
        transformResponse: (response) => response?.source ?? null,
      }),

      fetchUseCaseApis: builder.query({
        query: (useCaseId) => ({
          url: `${import.meta.env.VITE_API_PATH}/clara/usecases/${encodeURIComponent(useCaseId)}/apis`,
          method: "GET",
        }),
        // getDatabricksUseCaseApis returns { apis: [...] } — flat array, no SOR data
        transformResponse: (response) => response?.apis ?? [],
        // Clara data is nearly static — keep in cache for 10 minutes so reopening
        // the same use case panel within a session is served instantly.
        keepUnusedDataFor: 600,
      }),

      fetchUseCaseApisSor: builder.query({
        query: (useCaseId) => ({
          url: `${import.meta.env.VITE_API_PATH}/clara/usecases/${encodeURIComponent(useCaseId)}/sor`,
          method: "GET",
        }),
        // getDatabricksUseCaseApisSor returns { sorData: [...] }
        transformResponse: (response) => response?.sorData ?? [],
        keepUnusedDataFor: 600,
      }),

      fetchOperationDetails: builder.query({
        async queryFn({ method, path }) {
          const requestUrl = new URL(
            "/marketplace/apic-microservice/v3/operation-details",
            resolveOperationDetailsOrigin()
          );

          requestUrl.searchParams.set("method", method);
          requestUrl.searchParams.set("path", path);

          try {
            const response = await fetch(requestUrl.toString(), {
              method: "GET",
              headers: {
                Accept: "application/json",
              },
            });

            const contentType = response.headers.get("content-type") ?? "";
            const responseBody = contentType.includes("application/json")
              ? await response.json()
              : await response.text();

            if (!response.ok) {
              return {
                error: {
                  status: response.status,
                  data: responseBody,
                },
              };
            }

            if (!contentType.includes("application/json")) {
              return {
                error: {
                  status: "PARSING_ERROR",
                  error: `Expected application/json but received ${contentType || "unknown content type"}`,
                  data: responseBody,
                },
              };
            }

            return { data: responseBody };
          } catch (error) {
            return {
              error: {
                status: "FETCH_ERROR",
                error: error instanceof Error ? error.message : "Failed to fetch operation details",
              },
            };
          }
        },
        // Cache for 10 minutes — operation details don't change within a session.
        keepUnusedDataFor: 600,
      }),

      fetchRelatedGuides: builder.query({
        query: () => ({
          url: `${import.meta.env.VITE_API_PATH}/related-guides`,
          method: "GET",
        }),
        transformResponse: (response) =>
          Array.isArray(response) ? response : [],
        keepUnusedDataFor: 600,
      }),
    };
  },
});

export const {
  useFetchClaraDomainsQuery,
  useFetchClaraDomainUseCasesQuery,
  useFetchUseCaseApisQuery,
  useFetchUseCaseApisSorQuery,
  useFetchOperationDetailsQuery,
  useFetchRelatedGuidesQuery,
} = claraApi;
export { claraApi };
